// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The on-the-wire format for CRDT operations. A single op (or a whole op-log
 * bundle for a newcomer) is serialised to JSON bytes, encrypted as one AES-GCM
 * blob, and framed as [iv(12) | ciphertext]. Keeping serialisation and framing
 * pure means the entire format is unit-testable without any network or crypto.
 *
 * The transport peer id (from trystero) is the authoritative sender; the author
 * field inside a shape is convenience for z-order tie-breaking and export only.
 */
import type { Op } from './types.ts';

export const IV_LEN = 12;

const enc = new TextEncoder();
const dec = new TextDecoder();

/** One op → JSON bytes. */
export function encodeOp(op: Op): Uint8Array {
  return enc.encode(JSON.stringify(op));
}

/** JSON bytes → one op. Throws on malformed input. */
export function decodeOp(bytes: Uint8Array): Op {
  return JSON.parse(dec.decode(bytes)) as Op;
}

/** A whole op-log → JSON bytes (the full-state sync bundle). */
export function encodeOps(ops: Op[]): Uint8Array {
  return enc.encode(JSON.stringify(ops));
}

/** JSON bytes → an op-log. Throws on malformed input, or if it is not an array. */
export function decodeOps(bytes: Uint8Array): Op[] {
  const v = JSON.parse(dec.decode(bytes));
  if (!Array.isArray(v)) throw new Error('sync bundle is not an array');
  return v as Op[];
}

/** Prepend the 12-byte IV to the ciphertext to make the wire frame. */
export function packFrame(iv: Uint8Array, cipher: Uint8Array): Uint8Array {
  if (iv.length !== IV_LEN) throw new Error(`iv must be ${IV_LEN} bytes`);
  const frame = new Uint8Array(IV_LEN + cipher.length);
  frame.set(iv, 0);
  frame.set(cipher, IV_LEN);
  return frame;
}

/** Split a wire frame back into its IV and ciphertext. */
export function unpackFrame(frame: Uint8Array): { iv: Uint8Array; cipher: Uint8Array } {
  if (frame.length < IV_LEN) throw new Error('frame too short');
  return { iv: frame.subarray(0, IV_LEN), cipher: frame.subarray(IV_LEN) };
}
